<template>
  <kendo-window
      ref="kendoWindowRef"
      :title="'2차 인증'"
      @open="onOpenWindow"
      @close="onCloseWindow"
      :modal="true"
      :width="'420px'"
      style="display:none;">
    <div class="login_auth_wrap">
      <p class="login_auth_desc">
        <template v-if="authSent">
          등록된 이메일로 인증번호를 발송했습니다.<br />
          <span v-if="maskedEmail" class="login_auth_email">{{ maskedEmail }}</span>
          메일을 확인하신 후 인증번호를 입력해 주세요.
        </template>
        <template v-else>
          본인 확인을 위한 2차 인증입니다.<br />
          '전송' 버튼을 누르면 등록된 이메일로 인증번호가 발송됩니다.
        </template>
      </p>
      <div class="login_auth_code_row">
        <div class="login_auth_code_box" :class="{ 'is_expired': timerSeconds <= 0 }">
          <input
              ref="authInputRef"
              type="text"
              class="login_auth_code_input"
              v-model="authNumber"
              maxlength="4"
              inputmode="numeric"
              placeholder="인증번호 4자리 입력"
              :disabled="timerSeconds <= 0"
              @input="onInputAuthNumber"
          />
          <span v-if="timerSeconds > 0" class="login_auth_timer">{{ timerDisplay }}</span>
        </div>
        <button
            type="button"
            class="mid_btn"
            @click="sendAuth"
            :disabled="authSent && timerSeconds > 0"
        >{{ authSent ? '재전송' : '전송' }}</button>
      </div>
      <div class="login_auth_msg">
        <span v-if="authSent && timerSeconds > 0" class="msg_info">
          인증번호가 발송되었습니다. 유효시간 내에 입력해 주세요.
        </span>
        <span v-else-if="authSent && timerSeconds <= 0" class="msg_red">
          유효시간이 초과하였습니다. 재전송해 주세요.
        </span>
      </div>
      <div class="button_area w100 mgt30">
        <ul>
          <li class="w30">
            <button class="large_btn" @click="onClickCancel">취소</button>
          </li>
          <li class="w30">
            <button class="large_btn orange ml5" @click="confirmAuth">확인</button>
          </li>
        </ul>
      </div>
    </div>
  </kendo-window>
</template>
<script>
import ApiConfig from '@/api/api.config'
import ApiUtil from '@/api/api.util'
import EtnersEncDecUtil from '@/common/etners.encDec.util'

export default {
  name: 'LoginAuthWindow',
  props: {
    userId: { type: String, default: '' },
    loginUserToken: { type: String, default: '' },
  },
  data () {
    return {
      authNumber: '',
      authSent: false,
      maskedEmail: '',
      authTimer: null,
      authExpireAt: 0,
      timerSeconds: 0,
      confirming: false,
    }
  },
  computed: {
    timerDisplay () {
      const min = Math.floor(this.timerSeconds / 60)
      const sec = this.timerSeconds % 60
      return `${min}:${String(sec).padStart(2, '0')}`
    },
  },
  mounted () {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy () {
    this.clearTimer()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    onOpenWindow: function () {
      const vm = this
      vm.authNumber = ''
      vm.authSent = false
      vm.maskedEmail = ''
      vm.timerSeconds = 0
      vm.confirming = false
      vm.clearTimer()
    },

    onCloseWindow: function () {
      this.clearTimer()
    },

    sendAuth: async function () {
      const vm = this
      vm.clearTimer()

      const publicKey = vm.$store.state.encStoreModule.enc.data
      const userIdRSA = await EtnersEncDecUtil.encryptRSA(vm.userId, publicKey)
      const url = `${ApiConfig.eklDomain}/v1/ebmp/ai/manager/auth/sendLoginAuthNumber.json`
      const param = {
        userId: userIdRSA,
        loginUserToken: vm.loginUserToken,
        transferMethod: 'email',
      }

      ApiUtil.post(url, param).then(response => {
        if (response.data === undefined || response.data.resultStatus === undefined) {
          kendo.alert('데이터를 정상적으로 받아오지 못했습니다.')
          return false
        }
        if (response.data.resultStatus.messageCode !== '2000') {
          kendo.alert(response.data.resultStatus.messageText)
          return false
        }
        const resultData = response.data.resultData
        vm.maskedEmail = (resultData && resultData.email) ? resultData.email : ''
        vm.authSent = true
        vm.authNumber = ''
        kendo.alert('인증번호가 전송되었습니다. 유효시간은 1분 30초입니다.')
        vm.startTimer()
      })
    },

    onInputAuthNumber: function () {
      const vm = this
      // 숫자 외 문자 제거
      const onlyNum = (vm.authNumber || '').replace(/[^0-9]/g, '').slice(0, 4)
      if (onlyNum !== vm.authNumber) {
        vm.authNumber = onlyNum
      }
      // 4자리 입력 완료 시 자동 인증
      if (vm.authNumber.length === 4 && vm.timerSeconds > 0 && !vm.confirming) {
        vm.confirmAuth()
      }
    },

    confirmAuth: async function () {
      const vm = this
      if (vm.confirming) {
        return false
      }
      if (!vm.authNumber || vm.authNumber.length !== 4) {
        kendo.alert('인증번호 4자리를 입력해 주세요.')
        return false
      }
      if (vm.timerSeconds <= 0) {
        kendo.alert('인증번호 유효시간이 만료되었습니다. 다시 전송해 주세요.')
        return false
      }

      const publicKey = vm.$store.state.encStoreModule.enc.data
      const userIdRSA = await EtnersEncDecUtil.encryptRSA(vm.userId, publicKey)
      const url = `${ApiConfig.eklDomain}/v1/ebmp/ai/manager/auth/confirmLoginAuthNumber.json`
      const param = {
        authNumber: vm.authNumber,
        userId: userIdRSA,
        loginUserToken: vm.loginUserToken,
      }

      vm.confirming = true
      ApiUtil.post(url, param).then(response => {
        if (response.data === undefined || response.data.resultStatus === undefined) {
          vm.confirming = false
          vm.alertThenFocusInput('데이터를 정상적으로 받아오지 못했습니다.')
          return false
        }
        if (response.data.resultStatus.messageCode !== '2000') {
          vm.confirming = false
          vm.alertThenFocusInput(response.data.resultStatus.messageText)
          return false
        }
        vm.clearTimer()
        // 서버가 2차 인증 성공 시 재발급한 실사용 토큰(mfa=Y)으로 기존 토큰을 교체한다.
        const resultData = response.data.resultData
        const newLoginUserToken = (resultData && resultData.loginUserToken) ? resultData.loginUserToken : ''
        if (newLoginUserToken) {
          sessionStorage.setItem('loginUserToken', newLoginUserToken)
        }
        // 인증 성공 → 부모(Login.vue)에서 후속 로그인 처리 진행
        vm.$emit('verified', newLoginUserToken)
        vm.closeWindow()
      }).catch(() => {
        vm.confirming = false
      })
    },

    alertThenFocusInput: function (message) {
      const vm = this
      const focusInput = function () {
        setTimeout(function () {
          const el = vm.$refs.authInputRef
          if (el) {
            el.focus()
            el.select()
          }
        }, 50)
      }
      const dialog = kendo.alert(message)
      if (dialog && typeof dialog.bind === 'function') {
        dialog.bind('close', focusInput)
      } else {
        focusInput()
      }
    },

    onClickCancel: function () {
      this.closeWindow()
    },

    closeWindow: function () {
      this.$refs.kendoWindowRef.kendoWidget().close()
    },

    startTimer: function () {
      const vm = this
      vm.clearTimer()
      vm.authExpireAt = Date.now() + 90 * 1000 //90초
      vm.tickTimer()
      vm.authTimer = setInterval(vm.tickTimer, 500)
    },

    tickTimer: function () {
      const vm = this
      const remaining = Math.max(0, Math.ceil((vm.authExpireAt - Date.now()) / 1000))
      vm.timerSeconds = remaining
      if (remaining <= 0) {
        vm.clearTimer()
      }
    },

    clearTimer: function () {
      if (this.authTimer) {
        clearInterval(this.authTimer)
        this.authTimer = null
      }
    },

    handleVisibilityChange: function () {
      if (!document.hidden && this.authTimer) {
        this.tickTimer()
      }
    },
  },
}
</script>
<style scoped>
.login_auth_wrap {
  padding: 4px 4px 0;
}
.login_auth_desc {
  font-size: 14px;
  color: #4d4d4d;
  line-height: 1.6;
  margin-bottom: 18px;
}
.login_auth_email {
  display: inline-block;
  font-weight: 600;
  color: #fe5f40;
  margin-right: 4px;
}
.login_auth_code_row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.login_auth_code_box {
  display: inline-flex;
  align-items: center;
  flex: 1;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #c4c4c4;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s linear, box-shadow 0.15s linear;
}
.login_auth_code_box:focus-within {
  border-color: #fe5f40;
  box-shadow: 0 0 0 3px rgba(254, 95, 64, 0.12);
}
.login_auth_code_box.is_expired {
  border-color: #e74c3c;
  background: #fff7f5;
}
.login_auth_code_input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  height: 100%;
  font-size: 14px;
  letter-spacing: 4px;
  color: #333;
  padding: 0;
}
.login_auth_code_input::placeholder {
  letter-spacing: 0;
  color: #a0a0a0;
}
.login_auth_code_input:disabled {
  color: #999;
}
.login_auth_timer {
  margin-left: 8px;
  font-size: 13px;
  color: #fe5f40;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.login_auth_msg {
  margin-top: 8px;
  min-height: 18px;
  line-height: 1.5;
}
.login_auth_msg .msg_info {
  color: #4d4d4d;
  font-size: 13px;
}
.login_auth_msg .msg_red {
  color: #e74c3c;
  font-size: 13px;
}
</style>
